import React, { useState, useEffect, useRef } from "react";
import { publicChatApi, sessionManager } from "../services/api";
import LoadingSpinner from "./LoadingSpinner";
import AgenticChecklist from "./AgenticChecklist";
import PublicAgenticResponse from "./PublicAgenticResponse";
import ReactMarkdown from "react-markdown";
import AiChatIcon from "../assets/aiChatIcon.png";

import { Send, Bot, User, Shield, Sparkles } from "lucide-react";

interface PublicChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  metadata?: {
    type?:
      | "customer_service_rag"
      | "error_response"
      | "offline_response"
      | "offline_notice"
      | "fallback_response"
      | "agentic_notice"
      | "agentic_parameter_collection"
      | "agentic_ready"
      | "agentic_action_result"
      | "welcome";
    meta?: any;
    confidence_score?: number;
    context_chunks_used?: number;
    source_documents?: any[];
    response_metadata?: any;
    search_metadata?: any;
    intent?: {
      type: string;
      action: string;
      topic: string;
      entities?: Record<string, any>;
    };
    requires_auth?: boolean;
    eventEmitted?: boolean;
    [key: string]: any;
  };
  created_at: string;
}

interface Props {
  isEmbedded?: boolean;
  className?: string;
  onSessionCreated?: (sessionId: string) => void;
  onAgenticNotice?: (data: any) => void;
  onInitError?: (error: string) => void;
  businessId?: string;
}

const PublicChat: React.FC<Props> = ({
  isEmbedded = false,
  className = "",
  onSessionCreated,
  onAgenticNotice,
  onInitError,
  businessId,
}) => {
  // Chat state
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<PublicChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isAgenticVerified, setIsAgenticVerified] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    console.log("📊 Messages array:", messages);
    console.log("📊 Message count:", messages.length);

    // Check for invalid messages
    const invalidMessages = messages.filter((msg, idx) => {
      if (!msg || !msg.role || !msg.content) {
        console.warn(`⚠️ Invalid message at index ${idx}:`, msg);
        return true;
      }
      return false;
    });

    if (invalidMessages.length > 0) {
      console.error("❌ Found", invalidMessages.length, "invalid messages");
    }
  }, [messages]);

  useEffect(() => {
    initializeSession();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Check initial verification status
  useEffect(() => {
    const isVerified = sessionManager.isAgenticVerified();
    setIsAgenticVerified(isVerified);
  }, []);

  useEffect(() => {
    console.log("📊 ===== MESSAGES STATE UPDATE =====");
    console.log("Total messages:", messages.length);

    const invalidMessages: any[] = [];

    messages.forEach((msg, idx) => {
      const isValid = msg && msg.role && msg.content;

      console.log(`Message ${idx}:`, {
        id: msg?.id,
        role: msg?.role,
        hasContent: !!msg?.content,
        contentLength: msg?.content?.length,
        isValid,
        rawMessage: msg,
      });

      if (!isValid) {
        invalidMessages.push({ index: idx, message: msg });
        console.error(`❌ Invalid message at index ${idx}:`, msg);
      }
    });

    if (invalidMessages.length > 0) {
      console.error(
        "❌ Found",
        invalidMessages.length,
        "invalid messages:",
        invalidMessages,
      );
    } else {
      console.log("✅ All messages are valid");
    }

    console.log("===================================");
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        200,
      )}px`;
    }
  }, [newMessage]);

  // Listen for external events
  useEffect(() => {
    const handleExternalMessage = (event: any) => {
      if (event.detail?.message) {
        setNewMessage(event.detail.message);
        setTimeout(() => {
          handleSendMessage(new Event("submit") as any, event.detail.message);
        }, 100);
      }
    };

    const handleAgenticReplay = (event: any) => {
      if (event.detail?.response) {
        const replayMessage: PublicChatMessage = {
          id: event.detail.response.id,
          role: "assistant",
          content: event.detail.response.content,
          created_at: event.detail.response.created_at,
          metadata: event.detail.response.metadata,
        };
        setMessages((prev) => [...prev, replayMessage]);
      }
    };

    const handleVerificationChange = () => {
      const isVerified = sessionManager.isAgenticVerified();
      setIsAgenticVerified(isVerified);
    };

    window.addEventListener("sendPublicMessage", handleExternalMessage);
    window.addEventListener("agenticActionReplayed", handleAgenticReplay);
    window.addEventListener(
      "agenticVerificationChanged",
      handleVerificationChange,
    );

    return () => {
      window.removeEventListener("sendPublicMessage", handleExternalMessage);
      window.removeEventListener("agenticActionReplayed", handleAgenticReplay);
      window.removeEventListener(
        "agenticVerificationChanged",
        handleVerificationChange,
      );
    };
  }, []);

  const initializeSession = async () => {
    try {
      setIsInitializing(true);

      // Gather visitor information including businessId
      const visitorInfo: Record<string, any> = {
        name: "Anonymous",
        page_url: window.location.href,
        referrer: document.referrer,
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      // Add businessId if provided (API expects camelCase 'businessId')
      if (businessId) {
        visitorInfo.businessId = businessId;
      }

      const response = await publicChatApi.createSession({
        visitor_info: visitorInfo,
      });

      const newSessionId = response.data.session.id;
      setSessionId(newSessionId);

      onSessionCreated?.(newSessionId);

      const event = new CustomEvent("publicChatSessionCreated", {
        detail: { sessionId: newSessionId },
      });
      window.dispatchEvent(event);

      // Don't show welcome message - clean start like ChatGPT
      setMessages([]);
    } catch (error: any) {
      console.error("Failed to initialize chat session:", error);

      // Extract user-friendly error message
      let errorMessage =
        "Unable to start chat session. Please refresh the page to try again.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.details?.length > 0) {
        // Handle validation errors
        const detail = error.response.data.details[0];
        errorMessage = `Configuration error: ${detail.msg || "Invalid parameters"}. Please contact support.`;
      } else if (
        error.response?.status === 401 ||
        error.response?.status === 403
      ) {
        errorMessage =
          "This chat service is not available. Please check the business configuration.";
      }

      // Notify parent component about the error
      onInitError?.(errorMessage);

      setMessages([
        {
          id: Date.now().toString(),
          role: "system",
          content: errorMessage,
          created_at: new Date().toISOString(),
          metadata: { type: "error_response" },
        },
      ]);
    } finally {
      setIsInitializing(false);
    }
  };

  // ===================================
  // 🔐 HANDLE SEND MESSAGE - MATCHES Chat.tsx PATTERN
  // ===================================
  const handleSendMessage = async (
    e: React.FormEvent,
    messageOverride?: string,
  ) => {
    e.preventDefault();

    const messageContent = messageOverride || newMessage.trim();
    if (!messageContent || isLoading || !sessionId) return;

    setNewMessage("");
    setIsLoading(true);

    // User message
    const userMessage: PublicChatMessage = {
      id: Date.now().toString(),
      role: "user",
      content: messageContent,
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);

    try {
      console.log("📡 Sending message to API...");

      const response = await publicChatApi.sendMessage(sessionId, {
        message: messageContent,
        visitor_info: {},
      });

      console.log("📨 Full API Response:", response.data);

      // ✅ HANDLE BOTH RESPONSE STRUCTURES
      let assistantMessage: PublicChatMessage;

      if (response.data.assistant_message) {
        // Standard response structure
        assistantMessage = response.data.assistant_message;
        console.log("📨 Using assistant_message from response");
      } else if (
        response.data.message &&
        typeof response.data.message === "object"
      ) {
        // Inline auth response structure (message is an object)
        assistantMessage = response.data.message;
        console.log("📨 Using message from response (inline auth)");
      } else {
        console.error("❌ Unknown response structure:", response.data);
        throw new Error("Invalid response structure");
      }

      console.log("📨 Extracted assistant message:", assistantMessage);

      // ✅ Validate and normalize message
      if (!assistantMessage.role) {
        console.warn("⚠️ Adding missing role field");
        assistantMessage.role = "assistant";
      }

      if (!assistantMessage.content) {
        console.error("❌ Message missing content");
        throw new Error("Message missing content");
      }

      if (!assistantMessage.id) {
        console.warn("⚠️ Adding missing id field");
        assistantMessage.id = Date.now().toString();
      }

      if (!assistantMessage.created_at) {
        console.warn("⚠️ Adding missing created_at field");
        assistantMessage.created_at = new Date().toISOString();
      }

      // ✅ Add to messages
      console.log("✅ Adding validated message:", assistantMessage);
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error: any) {
      console.error("❌ Error in handleSendMessage:", error);

      // Only show error message for actual errors (not auth failures)
      const errorMessage: PublicChatMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content:
          "I encountered an issue processing your request. Please try again.",
        created_at: new Date().toISOString(),
        metadata: { type: "error_response" },
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const renderMessage = (message: PublicChatMessage) => {
    if (!message || !message.role) {
      console.warn("⚠️ Skipping invalid message:", message);
      return null;
    }
    const isUser = message.role === "user";
    const isSystem = message.role === "system";
    const isAgenticNotice = message.metadata?.type === "agentic_notice";
    const isAgenticChecklist =
      message.metadata?.type === "agentic_parameter_collection";
    const isAgenticResult = message.metadata?.type === "agentic_action_result";
    const isError = message.metadata?.type === "error_response";

    // Special handling for error messages - MATCHES INTERNAL CHAT
    if (isError) {
      return (
        <div key={message.id} className="flex justify-center my-6">
          <div className="max-w-2xl px-5 py-4 rounded-xl bg-red-500/10 border border-red-500/30 backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <span className="text-xl mt-0.5">⚠️</span>
              <div className="flex-1">
                <p className="text-sm text-red-300 leading-relaxed">
                  {message.content}
                </p>
              </div>
            </div>
            <div className="mt-2 text-xs text-red-400/60 text-center">
              {new Date(message.created_at).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
          </div>
        </div>
      );
    }

    // MATCHES INTERNAL CHAT LAYOUT
    return (
      <div
        key={message.id}
        className={`flex items-start gap-4 mb-8 group ${
          isUser ? "flex-row-reverse" : ""
        }`}
      >
        {/* Avatar - MATCHES INTERNAL CHAT */}
        <div className="flex-shrink-0">
          {isUser ? (
            <div className="w-11 h-11 rounded-xl bg-[#1e1535] flex items-center justify-center text-white text-base font-semibold shadow-lg ring-2 ring-[#4a2d6b]/30">
              <User className="w-6 h-6" />
            </div>
          ) : isAgenticNotice || isSystem ? (
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-xl shadow-lg ring-2 ring-amber-400/20">
              🔐
            </div>
          ) : isAgenticChecklist ? (
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center text-white text-xl shadow-lg ring-2 ring-yellow-400/20">
              📝
            </div>
          ) : isAgenticResult ? (
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-400 to-purple-600 flex items-center justify-center text-white text-xl shadow-lg ring-2 ring-indigo-400/20">
              ⚡
            </div>
          ) : (
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center shadow-lg ring-2 ring-purple-500/20 p-2">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
          )}
        </div>

        {/* Message Content - MATCHES INTERNAL CHAT */}
        <div
          className={`flex ${isUser ? "justify-end" : "justify-start"} w-full`}
        >
          {isAgenticChecklist ? (
            <div className="max-w-3xl">
              <AgenticChecklist content={message.content} />
            </div>
          ) : isAgenticResult ? (
            <div className="max-w-3xl">
              <PublicAgenticResponse message={message} />
            </div>
          ) : (
            <div className="flex flex-col">
              <div
                className={`px-5 py-3.5 rounded-2xl shadow-sm backdrop-blur-sm
                  ${
                    isUser
                      ? "bg-gradient-to-br from-purple-600 to-blue-600 text-white shadow-purple-600/20"
                      : isAgenticNotice || isSystem
                        ? "bg-amber-500/10 border border-amber-500/30 text-amber-100"
                        : "bg-[#0f172a]/80 border border-[#334155]/50 text-gray-100 shadow-xl"
                  }
                  ${
                    message.content.length < 50
                      ? "max-w-sm"
                      : message.content.length < 150
                        ? "max-w-xl"
                        : message.content.length < 300
                          ? "max-w-2xl"
                          : "max-w-3xl"
                  }
                `}
              >
                <div className="prose prose-invert prose-sm max-w-none">
                  <ReactMarkdown
                    components={{
                      p: ({ node, ...props }) => (
                        <p
                          className="mb-3 last:mb-0 leading-relaxed text-[15px]"
                          {...props}
                        />
                      ),
                      strong: ({ node, children, ...props }) => {
                        const text = children?.toString() || "";
                        if (
                          text.includes(":") ||
                          text.match(/^(Required|Optional|Please|You have)/i)
                        ) {
                          return (
                            <strong
                              className="block font-bold text-purple-300 text-base mb-2 mt-4 first:mt-0"
                              {...props}
                            >
                              {children}
                            </strong>
                          );
                        }
                        return (
                          <strong
                            className="font-bold text-white bg-purple-600/30 px-1.5 py-0.5 rounded"
                            {...props}
                          >
                            {children}
                          </strong>
                        );
                      },
                      code: ({ node, className, children, ...props }: any) => {
                        const isInline =
                          !className || !className.includes("language-");

                        if (isInline) {
                          return (
                            <code
                              className="px-2 py-0.5 bg-black/40 border border-white/10 rounded text-sm font-mono text-purple-200"
                              {...props}
                            >
                              {children}
                            </code>
                          );
                        }
                        return (
                          <code
                            className="block px-3 py-2 bg-black/40 border border-white/10 rounded my-2 text-sm font-mono overflow-x-auto"
                            {...props}
                          >
                            {children}
                          </code>
                        );
                      },
                      ul: ({ node, ...props }) => (
                        <ul className="space-y-1.5 my-2" {...props} />
                      ),
                      ol: ({ node, ...props }) => (
                        <ol className="space-y-1.5 my-2" {...props} />
                      ),
                      li: ({ node, children, ...props }) => {
                        const text = children?.toString() || "";
                        const hasCheckmark =
                          text.includes("✓") || text.includes("✔");

                        return (
                          <li
                            className="flex items-start text-[15px] leading-relaxed"
                            {...props}
                          >
                            {hasCheckmark ? (
                              <span className="mr-2 text-green-400 flex-shrink-0">
                                ✓
                              </span>
                            ) : (
                              <span className="mr-2 text-purple-400 flex-shrink-0">
                                •
                              </span>
                            )}
                            <span className="flex-1">{children}</span>
                          </li>
                        );
                      },
                      text: ({ node, ...props }) => {
                        const value = props.children?.toString() || "";

                        if (value.includes("✓") || value.includes("✔")) {
                          return (
                            <span className="text-green-400 font-bold">
                              {value}
                            </span>
                          );
                        }

                        return <>{value}</>;
                      },
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                </div>
              </div>

              {/* Timestamp - MATCHES INTERNAL CHAT */}
              <div
                className={`flex items-center gap-2 mt-2 ml-1 text-xs ${
                  isUser ? "text-purple-400/60 justify-end" : "text-gray-600"
                }`}
              >
                <span>
                  {new Date(message.created_at).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (isInitializing) {
    return (
      <div
        className={`flex items-center justify-center h-full bg-transparent ${className}`}
      >
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-gray-400 text-sm">Connecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col h-full bg-gradient-to-b from-[#0a0f1e] to-[#1a1f3a] ${className}`}
    >
      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto px-6 py-6 custom-scrollbar min-h-0">
        {messages.length === 0 && !isLoading ? (
          // ===================================
          // 🎨 CENTERED WELCOME SCREEN - MATCHES INTERNAL CHAT
          // ===================================
          <div className="flex items-center justify-center h-full">
            <div className="text-center py-12 w-full max-w-2xl mx-auto px-4">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center shadow-2xl shadow-purple-500/30 p-4">
                <img
                  src={AiChatIcon}
                  alt="AI"
                  className="w-full h-full object-contain brightness-0 invert"
                />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">
                👋 Welcome to Support
              </h3>
              <p className="text-gray-400 mb-2 max-w-md mx-auto">
                I'm your AI assistant, here to help 24/7
              </p>
              <p className="text-sm text-gray-500 max-w-md mx-auto">
                Ask me anything about our services, get support, or explore what
                we can do for you.
              </p>
            </div>
          </div>
        ) : (
          <div className="w-full max-w-4xl mx-auto">
            {messages
              .filter((msg) => {
                // Debug: Log every message
                console.log("🔍 Checking message:", {
                  id: msg?.id,
                  role: msg?.role,
                  hasContent: !!msg?.content,
                  contentLength: msg?.content?.length,
                  message: msg,
                });

                // Check if message is valid
                if (!msg) {
                  console.warn("❌ Message is null/undefined");
                  return false;
                }

                if (!msg.role) {
                  console.warn("❌ Message missing role:", msg);
                  return false;
                }

                if (!msg.content) {
                  console.warn("❌ Message missing content:", msg);
                  return false;
                }

                console.log("✅ Message is valid");
                return true;
              })
              .map(renderMessage)}

            {/* Loading indicator - MATCHES INTERNAL CHAT */}
            {isLoading && (
              <div className="flex items-start gap-4 mb-8">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center shadow-lg ring-2 ring-purple-500/20 p-2">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div className="max-w-md">
                  <div className="bg-[#0f172a]/80 border border-[#334155]/50 px-5 py-3.5 rounded-2xl shadow-xl backdrop-blur-sm">
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1.5">
                        <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"></div>
                        <div
                          className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"
                          style={{ animationDelay: "0.1s" }}
                        ></div>
                        <div
                          className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"
                          style={{ animationDelay: "0.2s" }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-400">Thinking...</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input Area - MATCHES INTERNAL CHAT STYLE */}
      <div className="flex-shrink-0 bg-[#0a0f1e]/50 backdrop-blur-xl border-t border-[#334155]/30 p-6">
        <div className="w-full flex justify-center">
          <div className="w-full max-w-4xl">
            <form
              onSubmit={handleSendMessage}
              className="relative flex items-center bg-[#1e293b]/80 border border-[#334155]/50 rounded-2xl px-4 py-3 shadow-xl shadow-purple-500/5 focus-within:border-purple-500/50 focus-within:shadow-purple-500/10 transition-all"
            >
              <textarea
                ref={textareaRef}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(e);
                  }
                }}
                placeholder="Ask anything..."
                className="flex-1 bg-transparent text-white placeholder-gray-500 resize-none outline-none px-3 py-2 max-h-[200px] leading-relaxed overflow-y-auto custom-scrollbar"
                disabled={isLoading}
                rows={1}
              />

              <button
                type="submit"
                disabled={!newMessage.trim() || isLoading}
                className="flex-shrink-0 ml-2 p-2.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg disabled:hover:shadow-md"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicChat;
